import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { Button, Card, Input, LoadingState, Modal } from '@/components/ui'
import { CheckCircle2, Upload, SkipForward, XCircle, RefreshCw } from 'lucide-react'

const UNIT_OPTIONS = ['Std', 'Tag', 'Pauschal', 'Stk']

interface ServiceCatalogSettings {
  defaultUnit: string
  confluenceSpaceKey?: string | null
  confluencePageId?: string | null
  confluenceTenantId?: string | null
}

interface ImportReportEntry {
  title: string
  category: string
  status: 'imported' | 'skipped' | 'error'
  reason?: string
}

interface ImportResult {
  pageTitle: string
  pageUrl: string
  total: number
  imported: number
  skipped: number
  errors: number
  newCategories: string[]
  report: ImportReportEntry[]
}

// Parse Confluence URL or plain input into import params
function parseConfluenceInput(raw: string): { pageId?: string; spaceKey?: string; pageTitle?: string } {
  const trimmed = raw.trim()
  // Full URL: https://…/wiki/spaces/SPACE/pages/123456/Title
  const urlMatch = trimmed.match(/\/pages\/(\d+)(?:\/|$)/)
  if (urlMatch) return { pageId: urlMatch[1] }
  // Bare numeric ID
  if (/^\d+$/.test(trimmed)) return { pageId: trimmed }
  // "SPACEKEY:Page Title" shorthand
  const colonMatch = trimmed.match(/^([A-Z0-9_-]{2,10}):(.+)$/)
  if (colonMatch) return { spaceKey: colonMatch[1], pageTitle: colonMatch[2].trim() }
  // Plain title — use default space
  return { pageTitle: trimmed }
}

export function ServicekatalogSettingsSection() {
  const queryClient = useQueryClient()
  const [defaultUnit, setDefaultUnit] = useState('Stk')
  const [saved, setSaved] = useState(false)

  // Confluence write-back target settings
  const [confluencePageId, setConfluencePageId] = useState('')
  const [confluenceSpaceKey, setConfluenceSpaceKey] = useState('')
  const [confluenceSettingsSaved, setConfluenceSettingsSaved] = useState(false)
  const [confluenceSyncResult, setConfluenceSyncResult] = useState<{ ok: boolean; message?: string } | null>(null)

  // Import state: single field accepts URL, Page-ID, or Page-Title
  const [confluenceInput, setConfluenceInput] = useState('')
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [showReport, setShowReport] = useState(false)

  const { data, isLoading } = useQuery<ServiceCatalogSettings>({
    queryKey: ['service-catalog-settings'],
    queryFn: () => apiClient.get('/service-catalog/settings').then((r) => r.data),
  })

  useEffect(() => {
    if (data) {
      setDefaultUnit(data.defaultUnit)
      setConfluencePageId(data.confluencePageId ?? '')
      setConfluenceSpaceKey(data.confluenceSpaceKey ?? 'SK')
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.put('/service-catalog/settings', { defaultUnit }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog-settings'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const saveConfluenceSettingsMutation = useMutation({
    mutationFn: () =>
      apiClient.put('/service-catalog/settings', {
        confluenceSpaceKey: confluenceSpaceKey.trim() || null,
        confluencePageId: confluencePageId.trim() || null,
      }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog-settings'] })
      setConfluenceSettingsSaved(true)
      setTimeout(() => setConfluenceSettingsSaved(false), 2500)
    },
  })

  const triggerSyncMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/service-catalog/sync-confluence', {}).then((r) => r.data as { ok: boolean; error?: string }),
    onSuccess: (d) => {
      setConfluenceSyncResult({ ok: d.ok, message: d.error })
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setConfluenceSyncResult({ ok: false, message: e.response?.data?.error ?? e.message ?? 'Fehler' })
    },
  })

  const importMutation = useMutation({
    mutationFn: () => {
      const params = parseConfluenceInput(confluenceInput)
      return apiClient
        .post('/service-catalog/import-confluence', params)
        .then((r) => r.data as ImportResult)
    },
    onSuccess: (data) => {
      setImportResult(data)
      setShowReport(true)
      queryClient.invalidateQueries({ queryKey: ['service-catalog'] })
      queryClient.invalidateQueries({ queryKey: ['service-catalog-categories'] })
    },
  })

  const parsedInput = confluenceInput.trim() ? parseConfluenceInput(confluenceInput) : null
  const inputHint = parsedInput?.pageId
    ? `Wird als Page-ID ${parsedInput.pageId} importiert`
    : parsedInput?.pageTitle
      ? `Sucht nach Titel "${parsedInput.pageTitle}"${parsedInput.spaceKey ? ` im Space ${parsedInput.spaceKey}` : ''}`
      : null

  if (isLoading) return <LoadingState />

  return (
    <>
      <Card>
        <Card.Header>
          <h3 className="text-sm font-semibold text-gray-900">Servicekatalog – Einstellungen</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Globaleinstellungen für neue Servicekatalog-Artikel. Pro Artikel überschreibbar.
          </p>
        </Card.Header>
        <Card.Body>
          <div className="max-w-xs space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Standard-Einheit</label>
              <select
                value={defaultUnit}
                onChange={(e) => setDefaultUnit(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
              >
                {UNIT_OPTIONS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Wird bei neuen Einträgen vorausgewählt und beim SAP-Push als SalesUnit übergeben.
              </p>
            </div>

            <Button
              variant="primary"
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saved
                ? <><CheckCircle2 className="w-4 h-4 mr-1 text-green-200" /> Gespeichert</>
                : 'Speichern'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="mt-4">
        <Card.Header>
          <h3 className="text-sm font-semibold text-gray-900">Confluence Write-Back</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Servicekatalog automatisch nach jeder Änderung in eine Confluence-Seite spiegeln.
            Konfiguriere die Ziel-Seite hier – der Sync läuft dann event-driven ohne manuellen Aufwand.
          </p>
        </Card.Header>
        <Card.Body>
          <div className="max-w-sm space-y-3">
            <Input
              label="Confluence Space Key"
              value={confluenceSpaceKey}
              onChange={(e) => setConfluenceSpaceKey(e.target.value)}
              placeholder="z.B. SK"
              hint="Key des Confluence-Spaces, in dem die Ziel-Seite liegt."
            />
            <Input
              label="Confluence Page ID"
              value={confluencePageId}
              onChange={(e) => setConfluencePageId(e.target.value)}
              placeholder="z.B. 123456789 (aus Confluence-URL)"
              hint="Numerische ID der Ziel-Seite. Leer lassen → wird automatisch erstellt."
            />

            {confluenceSyncResult && (
              <div className={`rounded p-2 text-xs ${confluenceSyncResult.ok ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                {confluenceSyncResult.ok ? 'Sync erfolgreich.' : `Fehler: ${confluenceSyncResult.message}`}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => { setConfluenceSyncResult(null); saveConfluenceSettingsMutation.mutate() }}
                disabled={saveConfluenceSettingsMutation.isPending}
              >
                {confluenceSettingsSaved
                  ? <><CheckCircle2 className="w-4 h-4 mr-1 text-green-200" /> Gespeichert</>
                  : 'Einstellungen speichern'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setConfluenceSyncResult(null); triggerSyncMutation.mutate() }}
                disabled={triggerSyncMutation.isPending}
                title="Servicekatalog jetzt nach Confluence synchronisieren"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${triggerSyncMutation.isPending ? 'animate-spin' : ''}`} />
                Jetzt synchronisieren
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="mt-4">
        <Card.Header>
          <h3 className="text-sm font-semibold text-gray-900">Confluence-Import</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Servicekatalog-Einträge aus einer Confluence-Seite importieren. Bereits vorhandene Einträge (gleicher Titel + Kategorie) werden übersprungen.
          </p>
        </Card.Header>
        <Card.Body>
          <div className="max-w-sm space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Confluence-URL oder Seiten-Titel
              </label>
              <input
                type="text"
                value={confluenceInput}
                onChange={(e) => setConfluenceInput(e.target.value)}
                placeholder="https://…/wiki/spaces/SK/pages/123456/… oder Seiten-Titel"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              {inputHint ? (
                <p className="text-xs text-brand-600 mt-1">→ {inputHint}</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">
                  Tipp: Confluence-URL direkt einfügen (enthält Page-ID) oder nur den Seiten-Titel eingeben.
                  Format „SPACEKEY:Seiten-Titel" sucht gezielt in einem Space.
                </p>
              )}
            </div>

            {importMutation.isError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                <p className="text-xs text-red-700 font-medium">Fehler beim Import</p>
                <p className="text-xs text-red-600 mt-0.5">
                  {(importMutation.error as { response?: { data?: { error?: string } }; message?: string })
                    ?.response?.data?.error ?? (importMutation.error as Error)?.message ?? 'Unbekannter Fehler'}
                </p>
              </div>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending || !confluenceInput.trim()}
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {importMutation.isPending ? 'Importiere…' : 'Servicekatalog importieren'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {importResult && (
        <Modal
          open={showReport}
          onClose={() => setShowReport(false)}
          title="Import-Report"
          size="lg"
        >
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{importResult.imported}</p>
                <p className="text-xs text-green-600">Importiert</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-gray-700">{importResult.skipped}</p>
                <p className="text-xs text-gray-500">Übersprungen</p>
              </div>
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-center">
                <p className="text-2xl font-bold text-red-700">{importResult.errors}</p>
                <p className="text-xs text-red-600">Fehler</p>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              Quelle:{' '}
              <a href={importResult.pageUrl} target="_blank" rel="noreferrer" className="text-brand-600 underline">
                {importResult.pageTitle}
              </a>
            </div>

            {importResult.newCategories.length > 0 && (
              <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3">
                <p className="text-xs font-medium text-yellow-800 mb-1">
                  Neue Kategorien angelegt ({importResult.newCategories.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {importResult.newCategories.map((c) => (
                    <span key={c} className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Detail list */}
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {importResult.report.map((entry, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-2">
                  {entry.status === 'imported' && <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />}
                  {entry.status === 'skipped' && <SkipForward className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
                  {entry.status === 'error' && <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{entry.title}</p>
                    {entry.category && <p className="text-xs text-gray-400">{entry.category}</p>}
                    {entry.reason && <p className="text-xs text-gray-400 italic">{entry.reason}</p>}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="primary" size="sm" onClick={() => setShowReport(false)}>
                Schließen
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
